import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/Input';

export interface AutocompleteSelectProps<T> {
  /** Currently committed id (the key returned by `getId`). */
  value: string;
  /** Display label for the currently committed value, used to seed the input. */
  initialLabel?: string;
  onSelect: (id: string, item: T | null) => void;
  onSearch: (q: string) => Promise<T[]>;
  renderItem: (item: T) => string;
  getId: (item: T) => string;
  placeholder?: string;
  disabled?: boolean;
  /** Stable cache key for react-query. */
  queryKey: string;
  'data-testid'?: string;
}

/**
 * Autocomplete combobox driven by an async `onSearch` function. Displays the
 * results in a popover under the input, picks one on click. Closes on outside
 * click. Cache TTL = 5 minutes.
 *
 * Used by the campaign wizard for BIG.call workspace/campaign pickers and
 * DataForSEO country/region pickers.
 */
export function AutocompleteSelect<T>({
  value,
  initialLabel,
  onSelect,
  onSearch,
  renderItem,
  getId,
  placeholder,
  disabled,
  queryKey,
  'data-testid': testId,
}: AutocompleteSelectProps<T>) {
  const [inputValue, setInputValue] = useState(initialLabel ?? '');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: items = [], isFetching } = useQuery({
    queryKey: ['autocomplete', queryKey, inputValue],
    queryFn: () => onSearch(inputValue),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  useEffect(() => {
    if (!value) setInputValue('');
  }, [value]);

  useEffect(() => {
    if (initialLabel && !inputValue) setInputValue(initialLabel);
  }, [initialLabel, inputValue]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(e.target.value);
    setOpen(true);
    if (!e.target.value) onSelect('', null);
  }

  function handleSelect(item: T) {
    setInputValue(renderItem(item));
    onSelect(getId(item), item);
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      <Input
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        data-testid={testId ? `${testId}-input` : undefined}
      />
      {open && (
        <div
          className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-60 overflow-y-auto"
          data-testid={testId ? `${testId}-dropdown` : undefined}
        >
          {isFetching && (
            <div className="px-3 py-2 text-sm text-gray-400">Chargement…</div>
          )}
          {!isFetching && items.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-400">Aucun résultat</div>
          )}
          {!isFetching &&
            items.map((item) => (
              <button
                key={getId(item)}
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 focus:bg-gray-50 outline-none"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(item);
                }}
                data-testid={testId ? `${testId}-option` : undefined}
              >
                {renderItem(item)}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
